
import axios from "axios";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import config from "../config";

const CartContext = createContext();

// Guest cart key in sessionStorage
const GUEST_CART_KEY = "guest_cart_v1";

// Helper functions for guest cart
const getGuestCart = () =>
  JSON.parse(sessionStorage.getItem(GUEST_CART_KEY) || "[]");
const setGuestCart = (cart) =>
  sessionStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
const clearGuestCart = () => sessionStorage.removeItem(GUEST_CART_KEY);

const GUEST_PET_DATA_KEY = "guest_pet_data_v1";

const saveGuestPetDataToStorage = (petData) => {
  if (petData) {
    sessionStorage.setItem(GUEST_PET_DATA_KEY, JSON.stringify(petData));
  } else {
    sessionStorage.removeItem(GUEST_PET_DATA_KEY);
  }
};

const getGuestPetDataFromStorage = () => {
  const data = sessionStorage.getItem(GUEST_PET_DATA_KEY);
  return data ? JSON.parse(data) : null;
};


export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [currentShopId, setCurrentShopId] = useState(null);
  const [currentShopName, setShopName] = useState(null);
  const [user, setUser] = useState(null);
  const [vendorID, setVendorId] = useState(null); // null = guest
  const [guestvendorid, setVendorIdguest] = useState(null);
  const isInitialMount = useRef(true);
  const isSavingToBackend = useRef(false);
  // FIX: Track when addToCart just made an API call to prevent duplicate saves
  const skipNextSaveRef = useRef(false);

  const [cartOpen, setCartOpen] = useState(false);

  //const [guestPetData, setGuestPetData] = useState(null);
  const [guestPetData, setGuestPetData] = useState(() => {
  const stored = getGuestPetDataFromStorage();
  console.log("📦 Initialized guestPetData from storage:", stored);
  return stored;
});

  // Get auth state from Redux
  const authState = useSelector((state) => state.auth);

  const formatDate = (date) =>
    date ? new Date(date).toISOString().split("T")[0] : null;

  // Removed duplicate initCart - consolidated logic below in main useEffect

  /** ---------------- Sync with auth state ---------------- */
  useEffect(() => {

    // Check if user is logged in by checking localStorage directly as fallback
    const tokenFromStorage = localStorage.getItem("token");
    const customerIdFromStorage = localStorage.getItem("customer_id");

    if (authState.isAuthenticated && authState.token && authState.customer_id) {
      // User is logged in - set user in cart context
     
      setUser({
        client_id: authState.customer_id,
        token: authState.token,
        phone: authState.phone,
        customer_data: authState.customer_data,
      });
    } else if (tokenFromStorage && customerIdFromStorage) {
      // Fallback: User is logged in but Redux state might not be updated yet
      console.log(
        "Setting user in cart context from localStorage fallback:",
        customerIdFromStorage
      );
      setUser({
        client_id: customerIdFromStorage,
        token: tokenFromStorage,
        phone: localStorage.getItem("phone"),
        customer_data: JSON.parse(
          localStorage.getItem("customer_data") || "{}"
        ),
      });
    } else if (tokenFromStorage) {
      // Try to get client_id from customer_data in localStorage
      const customerData = JSON.parse(
        localStorage.getItem("customer_data") || "{}"
      );
      if (customerData.id) {
        console.log(
          "Setting user in cart context from customer_data.id:",
          customerData.id
        );
        setUser({
          client_id: customerData.id,
          token: tokenFromStorage,
          phone: localStorage.getItem("phone"),
          customer_data: customerData,
        });
      }
    } else if (
      authState.isAuthenticated &&
      authState.token &&
      !authState.customer_id
    ) {
      // User is logged in but customer_id is not extracted from token
      try {
        const decoded = jwtDecode(authState.token);
       
        const cid =
          decoded?.data?.customer_data?._id ||
          decoded?.data?.customer_id ||
          decoded?.customer_id ||
          null;
        if (cid) {
          
          setUser({
            client_id: cid,
            token: authState.token,
            phone: authState.phone,
            customer_data: authState.customer_data,
          });
        } else {
        

          // Use customer_data.id from localStorage as client_id
          const customerData = JSON.parse(
            localStorage.getItem("customer_data") || "{}"
          );
          if (customerData.id) {
           
            setUser({
              client_id: customerData.id,
              token: authState.token,
              phone: authState.phone,
              customer_data: authState.customer_data,
            });
          } else if (authState.phone) {
          
            setUser({
              client_id: authState.phone,
              token: authState.token,
              phone: authState.phone,
              customer_data: authState.customer_data,
            });
          } else {
            setUser(null);
          }
        }
      } catch (e) {
        console.error("Failed to decode token in cart context:", e);
        setUser(null);
      }
    } else if (authState.isAuthenticated === false || !authState.token) {
      // User is not logged in - clear user in cart context
     
      setUser(null);
    }
    // If authState is still loading (undefined/null), don't change user state
  }, [
    authState.isAuthenticated,
    authState.token,
    authState.customer_id,
    authState.phone,
    authState.customer_data,
  ]);

  /** ---------------- Save cart to backend function ---------------- */
  const saveCartToBackend = useCallback(async () => {
    if (!user || !user.client_id || !user.token) return;

    // Prevent duplicate saves
    if (isSavingToBackend.current) {
    
      return;
    }

    isSavingToBackend.current = true;

    try {
      // Transform cart items to match API structure
      const orderItems = cartItems.map((item, index) => {
        const unitPrice =
          parseFloat(item.price) || parseFloat(item.unit_price) || 0;
        const quantity = item.quantity || 1;
        const totalPrice = unitPrice * quantity;
       

        return {
          item_type: item.item_type || item.service_type || "service",
          vendor_pet_id: item.vendor_pet_id || item.pet_id || null,
          service_id: item.service_id,

          vendor_name: item.vendor_name,

          technician_id: item.technician_id || null,
          description: item.name || item.description || "Service",
          start_date:
            item.start_date ||
            item.date ||
            new Date().toISOString().split("T")[0],
          start_time: item.start_time || item.time || "10:00 AM",
          frequency: item.frequency || "",
          duration: item.duration || 60,
          quantity: quantity,
          unit_price: unitPrice,
          discount: item.discount || 0,
          gst: item.gst || 0,
          total_price: totalPrice,
          addons: item.addons || [],
          // Preserve unique cart item ID and all date/time attributes
          cartItemId: item.cartItemId,
          end_date: item.end_date,
          time_range: item.time_range,
          slots: item.slots,
          mode: item.mode,
         // room_id: item.room_id,
         room_id: item.room_id || item.room_type_id, 
         room_type_id: item.room_type_id || item.room_id,
          room_name: item.room_name,
          pet: item.pet,
          petSize: item.petSize,
          service_type:item.service_type,
          allowedPetTypes: item.allowedPetTypes || item.pet_type || [],

          priceMeta: item.priceMeta,

         notes: item.notes || "",
          
        };
      });
      // Get vendor_id from cart items (should be the same for all items in cart)
      let vendorId = null;

      if (cartItems.length > 0) {
       vendorId =
  cartItems[0].vendor_id ||
  cartItems[0].vendor_client_id ||   // ✅ ADD THIS LINE
  cartItems[0].shop_id ||
  cartItems[0].sellerId ||
  cartItems[0].vendor?._id ||
  cartItems[0].vendor?.id;
      }

      if (!vendorId && currentShopId) {
  vendorId = currentShopId;
}

if (!vendorId) {
  console.error("No valid vendor_id found for cart save", cartItems);
  return;

}

      const requestBody = {
        order_type: "online",
        client_id: user.client_id,
        vendor_id: vendorId,
        order_items: orderItems,
      };
      setVendorIdguest(vendorId);

      await axios.post(
        `${config.API_BASE_URL}/customer/addOrUpdate`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
   
    } catch (err) {
      console.error("Failed to save cart to backend:", err);
      toast.error("Failed to save cart. Please try again.");
    } finally {
      isSavingToBackend.current = false;
    }
  }, [user, cartItems, currentShopId]);

  /** ---------------- Load cart on mount or when user changes ---------------- */
  useEffect(() => {
  const initCart = async () => {
    console.log(
      "Initializing cart for user:",
      user ? user.client_id : "guest"
    );

    // ---------------- GUEST USER ----------------
    if (!user) {
      const guestCart = getGuestCart();
     
      setCartItems(guestCart);
      return;
    }

    // ---------------- LOGGED-IN USER ----------------
    try {
      

      const res = await axios.get(
        `${config.API_BASE_URL}/customer/cart/${user.client_id}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const backendData = res.data?.data;

      // ✅ Safely load backend cart WITHOUT clearing existing cart
      if (backendData?.vendor_id) {
        setVendorId(backendData.vendor_id);
      }

      if (Array.isArray(backendData?.order_items)) {
        // FIX: Attach vendor_id to each item so it's available for booking/operations
        const itemsWithVendorId = backendData.order_items.map(item => ({
          ...item,
          vendor_id: item.vendor_id || backendData.vendor_id,
        }));
        
        // FIX: Skip the next auto-save since we just loaded from backend
        skipNextSaveRef.current = true;
        setCartItems(itemsWithVendorId);

      } else {
        console.warn(
          "⚠️ Backend cart missing order_items — preserving existing cart"
        );
      }

      // ---------------- MERGE GUEST CART ----------------
      const guestCart = getGuestCart();

      if (Array.isArray(guestCart) && guestCart.length > 0) {
        

        await mergeGuestCart(user, guestCart);

        // ❗ IMPORTANT: clear guest cart ONLY after successful merge
        clearGuestCart();
      }
    } catch (err) {
      console.error("❌ Failed to fetch cart:", err);

      // 🔒 FALLBACK — DO NOT CLEAR CART
      const fallbackCart = JSON.parse(
        localStorage.getItem("user_cart") || "[]"
      );

      if (fallbackCart.length > 0) {
        console.log("Using fallback cart from localStorage:", fallbackCart);
        setCartItems(fallbackCart);
      }
    }
  };

  // ✅ Run only when user state is finalized
  if (user !== undefined) {
    initCart();
  }
}, [user]);

/** ---------------- SAFE USER STATE TRANSITION EFFECT ---------------- */
useEffect(() => {
  if (user && cartItems.length > 0) {
    console.log(
      "User logged in with existing cart items — save handled by main effect"
    );
  }
}, [user, cartItems]);


useEffect(() => {
  saveGuestPetDataToStorage(guestPetData);
}, [guestPetData]);

  /** ---------------- Save guest cart to sessionStorage ---------------- */
  useEffect(() => {
    if (!user && !isInitialMount.current) {
      setGuestCart(cartItems);
    }
    if (isInitialMount.current) isInitialMount.current = false;
  }, [cartItems, user]);

  /** ---------------- Save logged-in user cart to backend ---------------- */
  useEffect(() => {
    console.log("Cart save effect triggered:", {
      hasUser: !!user,
      cartItemsLength: cartItems.length,
      isInitialMount: isInitialMount.current,
      userClientId: user?.client_id,
      skipNextSave: skipNextSaveRef.current,
    });

    // FIX: Skip this save if addToCart already made an API call
    if (skipNextSaveRef.current) {
      console.log("Skipping save - addToCart already synced with backend");
      skipNextSaveRef.current = false;
      // Still save to localStorage as backup
      if (user && cartItems.length > 0) {
        localStorage.setItem("user_cart", JSON.stringify(cartItems));
      }
      return;
    }

    // Only save if user is logged in, has items, and it's not the initial mount
    if (user && cartItems.length > 0 && !isInitialMount.current) {
    

      // Debounce the save to prevent multiple rapid calls
      const timeoutId = setTimeout(() => {
        
        saveCartToBackend();
        // Also save to localStorage as backup
        localStorage.setItem("user_cart", JSON.stringify(cartItems));
      
      }, 1000); // Increased to 1000ms debounce

      return () => {
      
        clearTimeout(timeoutId);
      };
    }
  }, [cartItems, user, saveCartToBackend]);

  /** ---------------- Save cart on page unload ---------------- */
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (user && cartItems.length > 0) {
        // Use synchronous XMLHttpRequest for beforeunload
        const xhr = new XMLHttpRequest();
        xhr.open(
          "POST",
          `${config.API_BASE_URL}/customer/addOrUpdate`,
          false
        );
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Authorization", `Bearer ${user.token}`);
        xhr.send(
          JSON.stringify({
            client_id: user.client_id,
            items: cartItems,
          })
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [user, cartItems]);

  /** ---------------- Generate unique cart item ID ---------------- */
  const generateCartItemId = (
    serviceId,
    serviceType,
    roomType,
    checkIn,
    checkOut,
    additionalDetails = {}
  ) => {
    let baseId = `${serviceId}_${serviceType}_${roomType || ""}_${
      checkIn || ""
    }_${checkOut || ""}`;

    // Include all date/time attributes - prioritize start_date/end_date for boarding
    if (additionalDetails.start_date || additionalDetails.end_date) {
      baseId += `_${additionalDetails.start_date || ""}_${
        additionalDetails.end_date || ""
      }`;
    }

    // Also include single date for grooming/daycare services
    if (additionalDetails.date) {
      baseId += `_${additionalDetails.date}`;
    }

    if (additionalDetails.time_range) {
      baseId += `_${additionalDetails.time_range}`;
    }

    if (additionalDetails.room_id || additionalDetails.room_name) {
      baseId += `_${additionalDetails.room_id || ""}_${
        additionalDetails.room_name || ""
      }`;
    }

    // Include pet information - prioritize pet_type for grooming services
    if (additionalDetails.pet_type) {
      baseId += `_${additionalDetails.pet_type}`;
    }

    // Also include pet and petSize for daycare services
    if (additionalDetails.pet || additionalDetails.petSize) {
      baseId += `_${additionalDetails.pet || ""}_${
        additionalDetails.petSize || ""
      }`;
    }

    if (additionalDetails.slots || additionalDetails.mode) {
      baseId += `_${JSON.stringify(additionalDetails.slots || [])}_${
        additionalDetails.mode || ""
      }`;
    }

    return baseId;
  };

  /** ---------------- Add item to cart ---------------- */
  const addToCart = async (item) => {
    const resolvedVendorId =
    item.vendor_id || item.shop_id || item.sellerId;
    if (!resolvedVendorId) {
      toast.error("Vendor information missing");
      return;
    }
    const additionalDetails = {
      date: item.date || item.start_date,
      time: item.time || item.time_range,
      pet: item.pet || item.selectedPetName,
      petSize: item.petSize,
      pet_type: item.pet_type,
      start_date: item.start_date,
      end_date: item.end_date,
      time_range: item.time_range,
      slots: item.slots,
      mode: item.mode,
      room_id: item.room_id,
      room_name: item.room_name,
    };
    console.log(
      "addToCart - Additional details being passed:",
      additionalDetails
    );
    // Create the additional details object
    const additionalDetailsForId = {
      date: item.date || item.start_date,
      time: item.time || item.time_range,
      pet: item.pet || item.selectedPetName,
      petSize: item.petSize,
      pet_type: item.pet_type,
      start_date: item.start_date,
      end_date: item.end_date,
      time_range: item.time_range,
      slots: item.slots,
      mode: item.mode,
      room_id: item.room_id,
      room_name: item.room_name,
      room_type_id: item.room_type_id ,
    };

    console.log(
      "addToCart - Additional details for ID generation:",
      additionalDetailsForId
    );

    // Try passing parameters individually to debug
    console.log("About to call generateCartItemId with:", {
      serviceId: item.service_id || item.id,
      serviceType: item.vendor_id,
      roomType: item.service_type,
      checkIn: item.room_type_id,
      checkOut: item.checkIn,
      additionalDetails: additionalDetailsForId,
    });

    const uniqueId = generateCartItemId(
      item.service_id || item.id,
      item.service_type,
      item.room_type_id ||item.room_id,
      item.checkIn,
      item.checkOut,
      additionalDetailsForId
    );

    // Check if cart has items from different shop
    if (cartItems.length > 0) {
      const currentVendorId = cartItems[0]?.vendor_id;
      const newVendorId = item.vendor_id;

      if (currentVendorId && newVendorId && currentVendorId !== newVendorId) {
        // Clear cart and add new item from different shop
        console.log(
          "Different shop detected, clearing cart and adding new item"
        );
        setCartItems([]);
        setVendorId(newVendorId);
        toast.info("Cart cleared to add items from new shop");
      }
    }
      
    // Guest
   // Guest
  if (!user) {
    const vendorId =
      item.vendor_id || item.shop_id || item.sellerId;

    if (!vendorId) {
      toast.error("Vendor information missing");
      return;
    }

  setCartItems((prev) => {
    const existingIndex = prev.findIndex(
      (i) => i.cartItemId === uniqueId
    );

    if (existingIndex !== -1) {
      const updated = [...prev];
      updated[existingIndex].quantity += 1;
      return updated;
    }

    return [
      ...prev,
      {
        ...item,
       // vendor_id: vendorId,  
       vendor_id: resolvedVendorId,
        quantity: 1,
        cartItemId: uniqueId,
        name: item.name || item.description || "Service",
         item_type: getItemType(item.service_type || item.item_type || ""),
        description: item.description || item.name || "Service",
        service_type: item.service_type || item.item_type || "service",
        petBreed: item.petBreed,
        petBreedId: item.petBreedId
      },
    ];
  });

    //setVendorIdguest(vendorId);     
     setVendorIdguest(resolvedVendorId);
     toast.success("Service added to cart!");
       return;
     }

    // Logged-in
    try {
      // Check if we need to clear cart for different shop (for logged-in users)
      if (cartItems.length > 0) {
        const currentVendorId = cartItems[0]?.vendor_id;
        const newVendorId = item.vendor_id;

        if (currentVendorId && newVendorId && currentVendorId !== newVendorId) {
          console.log(
            "Different shop detected for logged-in user, clearing cart"
          );
          setCartItems([]);
          toast.info("Cart cleared to add items from new shop");
        }
      }

      const unitPrice = parseFloat(item.price ?? item.unit_price) || 0;
      const quantity = 1;
      const totalPrice = unitPrice * quantity;

      let orderItem;
       
      
      if (
        (item.item_type || item.service_type).toLowerCase().includes("grooming")
      ) {
        // --- Grooming type: preserve the exact format ---
        orderItem = {
          service_id: item.service_id,   
          item_type: getItemType(item.service_type || item.item_type),
          name: item.name || item.description || "Service",
          price: item.price,
          description: item.description || item.name || "Service",
          duration: item.duration ,
          frequency: item.frequency ? item.frequency.toLowerCase() : "once",
          pet_type: item.pet_type,
          selectedPetName: item.selectedPetName,
          service_type: item.service_type || item.item_type || "service",
          start_date: item.start_date,
          time_range: item.time_range,
          vendor_id: item.vendor_id,
          vendor_name: item.vendor_name,
          vendor_pet_id: item.vendor_pet_id,

          room_id: item.room_id || item.room_type_id || null,
          room_type_id: item.room_type_id || item.room_id || null,
          room_name: item.room_name || null,
          allowedPetTypes: item.allowedPetTypes || item.pet_type || [],
          
          priceMeta: item.priceMeta,

          notes: item.notes || "",

        };
      } else {
        // --- Other types: default orderItem ---
        let startTime = item.start_time || "10:00 AM";
        let endTime = item.end_time || "11:00 AM";
        if (item.time_range) {
          const times = item.time_range.split("–").map((t) => t.trim());
          startTime = times[0];
          endTime = times[1] || times[0];
        }

        orderItem = {
          item_type: getItemType(item.service_type || item.item_type),
          name: item.name || item.description || "Service",
          vendor_pet_id: String(
            item.vendor_pet_id || item.pet_id || null
          ),
          service_id: String(item.service_id || item.id),
          vendor_name: item.vendor_name,
          technician_id: item.technician_id || null,
          description: item.description || item.name || "Service",
          start_date: item.start_date || null,
          end_date: item.end_date || null,
          start_time: startTime,
          end_time: endTime,
          service_type: item.service_type || item.item_type || "service",
          time_range: item.time_range || `${startTime} – ${endTime}`,
          room_name: item.room_name || null,
          room_id: item.room_id || null,
          room_type_id: item.room_type_id ? String(item.room_type_id) : null,
          duration: item.duration || 60,
          quantity,
          unit_price: unitPrice,
          discount: item.discount || 0,
          gst: item.gst || 0,
          total_price: totalPrice,
          addons: item.addons || [],

          priceMeta: item.priceMeta,

           notes: item.notes || "",

         // frequency: item.frequency || "", 
         allowedPetTypes: item.allowedPetTypes || item.pet_type || [],


        frequency: item.frequency ? item.frequency.toLowerCase() : "once",
        // ✅ CRITICAL FIX: Include cartItemId so the backend/frontend stays in sync
        //cartItemId: uniqueId,
        };
        console.log("Sending to backend with allowedPetTypes:", orderItem.allowedPetTypes);
      }

      const vendorId =
        item.vendor_id || item.shop_id || item.sellerId || currentShopId;
      if (!vendorId) return console.error("No vendor_id found");

      console.log("Order Item:", orderItem);

      const res = await axios.post(
        `${config.API_BASE_URL}/customer/addOrUpdate`,
        {
          order_type: "online",
          client_id: user.client_id,
          vendor_id: vendorId,

          order_items: [orderItem],
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

        if (res.data.data?.order_items) {
 const updatedItems = res.data.data.order_items.map(i => ({
   ...i,
    vendor_id: resolvedVendorId, // ✅ ensure persistence
  }));
  //const backendItems = res.data.data.order_items;

 // const enhancedItems = backendItems.map((backendItem) => {
   //return {
   //  ...backendItem,
    // vendor_id: resolvedVendorId,
    // priceMeta: item.priceMeta,           // Restore the full original priceMeta
     
    //};
  //});
  // FIX: Skip the next auto-save since we just synced with backend
  skipNextSaveRef.current = true;
  setCartItems(updatedItems);
  //setCartItems(enhancedItems);
}
      //if (res.data.data?.order_items) setCartItems(res.data.data.order_items);
      toast.success("Service added to cart!");
    } catch (err) {
    console.error("Failed to add item:", err);
     toast.error("Failed to add item to cart");
   }
  };

  /** ---------------- Merge guest cart after login ---------------- */
  const mergeGuestCart = async (loggedInUser, guestCartItems) => {
    try {

         // ✅ Capture pet data from guest cart BEFORE API call
    if (guestCartItems && guestCartItems.length > 0) {
      const allGuestPetsData = guestCartItems.map(item => ({
    service_id: item.service_id,
    service_name: item.name || item.service_type,
    pet_type: item.pet_type?.pet_type_id || item.pet_type?.name || item.pet_type,
    pet_type_name: item.pet_type?.name || null,
    pet_type_id: item.pet_type?.pet_type_id || item.pet_type,
    petSize: item.petSize?.size_label || item.petSize,
    petSize_id: item.petSize?.flps_pet_size_id || item.petSize,
    petSize_label: item.petSize?.size_label || null,
    breed: item.breed?.breed_id || item.breed?.breed_name || item.petBreed || null,
    breed_id: item.breed?.breed_id || item.petBreedId || null,
    breed_name: item.breed?.breed_name || item.petBreed || null,
    selectedPetName: item.selectedPetName,
  }));
  
  console.log("📦 Storing ALL guest pets data:", allGuestPetsData);
  setGuestPetData(allGuestPetsData);
    }

      const orderItems = guestCartItems.map((item) => {
        const unitPrice =
          parseFloat(item.price) || parseFloat(item.unit_price) || 0;
        const quantity = item.quantity || 1;
        const totalPrice = unitPrice * quantity;
        const cleanPetType = item.pet_type?.pet_type_id || item.pet_type || null;
        const cleanPetName = typeof item.selectedPetName === 'object' 
        ? (item.pet_name || "Pet") 
        : item.selectedPetName;
      
       const cleanPetSizeId =item.petSize?.flps_pet_size_id ||item.flps_pet_size_id ||
          (typeof item.petSize === "string" ? item.petSize : null);
       
      
        // ✅ Ensure item_type always valid (non-empty string)
       // const itemType =
        //  item.service_type?.trim() || item.item_type?.trim() || "service";
      const normalizeItemType = (serviceType, itemType) => {
      const val = (itemType || serviceType || "").toLowerCase();

      if (val.includes("groom")) return "grooming";
      if (val.includes("board")) return "boarding";
      if (val.includes("day")) return "day_care services";
      if (val.includes("vet")) return "vet_clinic";

      return "other";
    };

    // const itemType = normalizeItemType(item.service_type, item.item_type);
       const itemType = normalizeItemType(item.service_type, item.item_type);
    // ✅ vendor_pet_id must be a valid ObjectId or null
        const vendorPetId =
          item.vendor_pet_id && item.vendor_pet_id.length === 24
            ? item.vendor_pet_id
            : item.pet_id && item.pet_id.length === 24
            ? item.pet_id
            : null;
            
        
        return {
          item_type: itemType,
          vendor_pet_id: vendorPetId, // ✅ Send null instead of "default_pet"
          service_id: item.service_id || item.id,
          vendor_name: item.vendor_name,
          technician_id: item.technician_id || null,
          description: item.description || "Comfort Service",
          name: item.name || item.service_type || "Unnamed Service",
          vendor_name: item.vendor_name,
          service_type: item.service_type || item.item_type,

         // pet_type: item.pet_type,
         pet_type: cleanPetType,
         selectedPetName: cleanPetName,
         //selectedPetName: item.selectedPetName,
         // petSize: item.petSize,
          petSize: cleanPetSizeId,
          //petBreed: item.petBreed,
          petBreed: item.petBreedId,
          room_name: item.room_name,
          cartItemId: item.cartItemId,
          start_date:
            item.start_date ||
            item.date ||
            new Date().toISOString().split("T")[0],
          end_date: item.end_date || null, // ✅ Preserve end_date for boarding
          start_time: item.start_time || item.time || "10:00 AM",
          end_time: item.end_time || null, // ✅ Preserve end_time
          time_range: item.time_range || null, // ✅ Preserve time_range for grooming/daycare
          duration: item.duration || 60,
          quantity,
          unit_price: unitPrice,
          discount: item.discount || 0,
          gst: item.gst || 0,
          total_price: totalPrice,
          addons: item.addons || [],
          // ✅ Preserve all additional booking details
          slots: item.slots || null,
          mode: item.mode || null,
          room_id: item.room_id || null,
          room_type_id: item.room_type_id || null,
          pet: item.pet || null,
          frequency: item.frequency || "daily", 

          allowedPetTypes: item.allowedPetTypes || item.pet_type || [],
          
          priceMeta: item.priceMeta,

        // ✅ CRITICAL FIX: Include cartItemId so the backend/frontend stays in sync
        //cartItemId: uniqueId,
        };
      });

      // ✅ vendor_id selection
       let vendorId =
        guestCartItems[0]?.vendor_id ||
        guestCartItems[0]?.vendor_client_id || // ✅ ADD
        guestCartItems[0]?.shop_id ||
        guestCartItems[0]?.sellerId ||
         currentShopId ||
         null;

      if (!vendorId) {
        console.error("❌ No valid vendor_id found for guest cart merge");
        return;
      }

      const requestBody = {
        order_type: "online",
        client_id: loggedInUser.client_id,
        vendor_id: vendorId,
        order_items: orderItems,
      };

      console.log(
        "🔄 Merging guest cart - Request body:",
        JSON.stringify(requestBody, null, 2)
      );
      console.log(
        "🔍 Order items being sent:",
        orderItems.map((item) => ({
          name: item.name,
          start_date: item.start_date,
          end_date: item.end_date,
          time_range: item.time_range,
          cartItemId: item.cartItemId,
          frquency:item.frequency,
        }))
      );

      console.log("🔄 Merging guest cart - CLEANED Request body:", requestBody);

      const res = await axios.post(
        `${config.API_BASE_URL}/customer/addOrUpdate`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loggedInUser.token}`,
          },
        }
      );

      // ✅ Handle multiple possible response formats
      const updatedCart = res.data?.data?.order_items || res.data?.items || [];

      console.log(
        "✅ Guest cart merged successfully. Updated cart:",
        updatedCart
      );
      console.log(
        "🔍 Updated cart structure check:",
        updatedCart.map((item) => ({
          name: item.name,
          start_date: item.start_date,
          end_date: item.end_date,
          time_range: item.time_range,
          cartItemId: item.cartItemId,
        }))
      );
     
      const normalizedUpdatedCart = updatedCart.map((item) => ({
        ...item,
        vendor_id: vendorId, // ✅ CRITICAL FIX
      }));

      if (updatedCart.length > 0) {
        // FIX: Skip the next auto-save since we just synced with backend
        skipNextSaveRef.current = true;
        setCartItems(normalizedUpdatedCart);
        clearGuestCart(); // ONLY after success
      } else {
        console.error("❌ Merge failed, keeping guest cart intact");
      }
      //setCartItems(normalizedUpdatedCart);
          

      //setCartItems(updatedCart);
      //clearGuestCart();
    } catch (err) {
      console.error("Failed to merge guest cart:", err);
    }
  };

  /** ---------------- Remove item ---------------- */
  const removeFromCart = async (cartItemId, options = {}) => {
    const { silent = false } = options;

  // Find the item to see if it has a database _id
  const itemToRemove = cartItems.find(
    (item) => item._id === cartItemId || item.cartItemId === cartItemId
  );

  if (!itemToRemove) return;

  // GUEST LOGIC
  if (!user) {
    setCartItems(prev => prev.filter(i => i.cartItemId !== cartItemId));
     if (!silent) toast.success("Item removed");
    return;
  }

  // LOGGED-IN LOGIC
  try {
    const databaseId = itemToRemove._id; // The Mongo_ID

    if (databaseId) {
      // Use your new clearCart API for single item
      await axios.post(`${config.API_BASE_URL}/customer/cartclear`, {
        client_id: user.client_id,
        item_id: databaseId 
      }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      // FIX: Skip the next auto-save since we just synced with backend
      skipNextSaveRef.current = true;
      // Update local state by filtering out the _id
      setCartItems(prev => prev.filter(i => i._id !== databaseId));
    } else {
      // Fallback if _id isn't found for some reason - still need to sync
      setCartItems(prev => prev.filter(i => i.cartItemId !== cartItemId));
    }
    if (!silent) toast.success("Item removed");
    //toast.success("Item removed");
  } catch (err) {
    console.error("Remove failed:", err);
    //toast.error("Failed to remove item");
    if (!silent) toast.error("Failed to remove item");
  }
};

  /** ---------------- Update quantity ---------------- */
  
  const updateQuantity = (identifier, newQuantity) => {
  // 1. Handle deletion if quantity drops to 0
  if (newQuantity <= 0) {
    return removeFromCart(identifier);
  }

  // 2. Update local state
  setCartItems((prevItems) =>
    prevItems.map((item) => {
      // Check both database _id and custom uniqueId
      const isMatch = (item._id === identifier || item.cartItemId === identifier);
      
      if (isMatch) {
        // Calculate new total if price exists
        const unitPrice = parseFloat(item.unit_price || item.price || 0);
        return { 
          ...item, 
          quantity: newQuantity,
          total_price: unitPrice * newQuantity 
        };
      }
      return item;
    })
  );

  // NOTE: The saveCartToBackend useEffect will detect this change 
  // and sync the updated quantity to the server automatically.
};

  /** ---------------- Clear cart ---------------- */
  
   const clearCart = async () => {
  if (!user) {
    setCartItems([]);
    clearGuestCart();
    return;
  }

  try {
    // Sending ONLY client_id triggers the full clear logic ($set: { order_items: [] })
    await axios.post(`${config.API_BASE_URL}/customer/cartclear`, {
      client_id: user.client_id
    }, {
      headers: { Authorization: `Bearer ${user.token}` }
    });

    // FIX: Skip the next auto-save since we just cleared the cart on backend
    skipNextSaveRef.current = true;
    setCartItems([]);
    toast.success("Cart cleared");
  } catch (err) {
    console.error("Clear cart error:", err);
    toast.error("Could not clear cart");
  }
};

  const getItemType = (serviceName) => {
    // const groomingServices = [
    //   "Home Grooming",
    //   "Mobile Pet Grooming Van",
    //   "Grooming Salon",
    // ];
    // const boardingServices = ["Home Boarding", "Kennel Boarding", "Pet Resort"];
    // const dayCareServices = ["Day Care Services"];
    // const petVetServices = ["pet vet clinic","vetenary","vet clinic"];
    if (!serviceName) return "other"; // fallback if serviceName is undefined

    if (serviceName.toLowerCase().includes("grooming")) return "grooming";
    if (serviceName.toLowerCase().includes("boarding")) return "boarding";
    if (serviceName.toLowerCase().includes("day care")) return "day_care services";
   if (
    serviceName.toLowerCase().includes("vet")) 
    {
    return "vet_clinic";           // ← or "pet_vet_clinic" if you prefer
  }
    return "other"; // fallback
  };

  const createBooking = async (bookingDetails) => {
    if (!user) {
      toast.error("Cannot create booking: User not logged in");
      return null;
    }

    if (!cartItems || cartItems.length === 0) {
      toast.error("Cannot create booking: Cart is empty");
      return null;
    }

    try {
      const payload = {
        client_id: user.client_id,
        vendor_id: currentShopId,
        booking_date: bookingDetails.date,
        booking_time: bookingDetails.time,
        services: cartItems.map((item) => ({
          item_type: "grooming",
          vendor_pet_id: item.pet_id || null,
          quantity: item.quantity || 1,
          unit_price: parseFloat(item.price ?? item.unit_price) || 0,
          addons: item.addons || [],
          duration: item.duration || 60,
          description: item.description || "",
          sku_number: item.sku_number || "",
          discount: item.discount || 0,
          gst: item.gst || 0,
          total_price: item.total_price || 0,
        })),
        notes: bookingDetails.notes || "",
        payment_method: bookingDetails.payment_method || "cod", // optional
      };

      console.log("Creating booking payload:", payload);

      const res = await axios.post(
        `${config.API_BASE_URL}/customer/createBooking`,
        payload,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (res.data.status) {
        toast.success(
          `🎉 Booking created successfully! Order Number: ${res.data.data?.order_number}`
        );
        clearCart(); // clear cart after successful booking
        return res.data.data; // return booking info
      } else {
        toast.error(res.data.message || "Booking failed");
        return null;
      }
    } catch (err) {
      console.error("Booking creation failed:", err);
      toast.error("Failed to create booking. Please try again.");
      return null;
    }
  };

  /** ---------------- Utility functions ---------------- */
  const isInCart = (serviceId, serviceType, roomType, checkIn, checkOut) => {
    const uniqueId = generateCartItemId(
      serviceId,
      serviceType,
      roomType,
      checkIn,
      checkOut
    );
    return cartItems.some((i) => i.cartItemId === uniqueId);
  };

  const getCartItemQuantity = (
    serviceId,
    serviceType,
    roomType,
    checkIn,
    checkOut
  ) => {
    const uniqueId = generateCartItemId(
      serviceId,
      serviceType,
      roomType,
      checkIn,
      checkOut
    );
    const item = cartItems.find((i) => i.cartItemId === uniqueId);
    return item ? item.quantity : 0;
  };

  const getCartItemCount = () =>
    cartItems.reduce((total, i) => total + (i.quantity || 1), 0);
  const getCartTotal = () => {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((total, i) => {
      const price =
        i?.unit_price && i?.unit_price > 0
          ? parseFloat(i.unit_price)
          : parseFloat(i?.price ?? 0);
      const quantity = parseInt(i?.quantity) || 1;

      // For boarding items, calculate based on duration
      let itemTotal = price * quantity;
      if (i.item_type === "boarding" && i.start_date && i.end_date) {
        const start = new Date(i.start_date);
        const end = new Date(i.end_date);
        const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        itemTotal = price * diffDays;
      }

      return total + itemTotal;
    }, 0);
  };

  const logout = () => {
    setUser(null);
    clearCart();

    //clearGuestPetData(); // Add this
   //sessionStorage.removeItem(GUEST_PET_DATA_KEY); 
  };
 
  return (
    <CartContext.Provider
      value={{
        cartItems,
        currentShopId,
        currentShopName,
        user,
        vendorID,

        guestvendorid,
        setVendorIdguest,
        createBooking,
        setVendorId,
        setCartItems,
        setUser,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        isInCart,
        getCartItemQuantity,
        getCartItemCount,
        getCartTotal,
        mergeGuestCart,
        logout,

        cartOpen,
      setCartOpen,

      guestPetData,  // ✅ Add this
      clearGuestPetData: () => setGuestPetData(null), // ✅ Add clear function
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
